import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useQueries, useQuery } from '@tanstack/react-query'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import {
  ArrowLeft,
  ArrowRight,
  AtSign,
  Briefcase,
  CalendarDays,
  Check,
  CreditCard,
  Eye,
  EyeOff,
  Mail,
  ShieldCheck,
  Trophy,
  User,
  UsersRound,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Spinner } from '@/components/ui/spinner'
import { AuthLogoImage } from '@/components/auth/auth-logo-image'
import { authApi } from '@/services/auth'
import { buildSignupEmailVerificationPath, isSignupEmailOtpVerificationContext } from '@/lib/auth-verification'
import { orgTreeApi, type OrgTreeServiceInstance } from '@/services/org-tree'
import { buildPostSelectionPath } from '@/lib/auth-redirect'
import { useSelectedBackendServerUrl } from '@/lib/backend-servers'
import { getErrorMessage } from '@/lib/api'
import { AuthTermsDisclosure } from '@/components/auth/auth-terms-disclosure'
import { BackendServerSelector } from '@/components/auth/backend-server-selector'
import { useRefetchOnReconnect } from '@/hooks/use-refetch-on-reconnect'
import { resolveServiceInstanceAuthLogos } from '@/lib/auth-branding'
import { SportxWordmark, findClubSport, type ClubSport } from '@/components/auth/club-sports'
import {
  FieldError,
  FormSection,
  IconInput,
  PasswordChecklist,
  PasswordInput,
  PhoneInput,
  SignupSplitShell,
  type PasswordRule,
  type SignupBrand,
} from '@/components/auth/signup-split-shell'

const PHONE_NUMBER_SAMPLE_PLACEHOLDER = '9876543210'

const SIGNUP_ENV_ROOT_ORG_ID = String(import.meta.env.VITE_ROOT_ORGANIZATION_ID ?? '').trim()
const BUILD_MODE = String(import.meta.env.MODE ?? '').trim().toLowerCase()
const ENFORCE_ENV_ROOT_ORG_ID = BUILD_MODE === 'production' || BUILD_MODE === 'staging'

// Must match the root org and PRODUCT_PARENT_DEFAULT_IDENTIFIERS seeded by the backend bootstrap.
// Identifiers that don't exist on the selected server are left out of the dropdown.
const SIGNUP_PARENT_ORGANIZATION_IDENTIFIERS = [
  'eln-org',
  'services-parent-org',
  'security-parent',
  'cleaning-parent-org',
  'home-services-parent-org',
  'e-commerce-parent-org',
  'academic-parent-org',
  'club-parent-org',
  'community-parent-org',
]
// Club signups need the club provisioning flow (sport, branch, club app), not a plain organization.
const CLUB_PARENT_ORGANIZATION_IDENTIFIER = 'club-parent-org'

const companySignupSchema = z
  .object({
    parent_org_reference: z.string().trim().optional().or(z.literal('')),
    company_name: z.string().trim().min(1, 'Organization name is required'),
    identifier: z.string().trim().min(1, 'Identifier is required'),
    owner_name: z.string().trim().min(1, 'Owner name is required'),
    email: z.string().email('Please enter a valid email address'),
    phone_number: z.string().trim().min(1, 'Phone number is required'),
    country_id: z.string().trim().min(1, 'Country is required'),
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Password must contain uppercase, lowercase, and number'),
    password_confirm: z.string(),
  })
  .refine((data) => data.password === data.password_confirm, {
    message: 'Passwords do not match',
    path: ['password_confirm'],
  })

type CompanySignupFormData = z.infer<typeof companySignupSchema>

const PASSWORD_RULES: PasswordRule[] = [
  { label: 'At least 8 characters', test: (value) => value.length >= 8 },
  { label: 'An uppercase letter', test: (value) => /[A-Z]/.test(value) },
  { label: 'A lowercase letter', test: (value) => /[a-z]/.test(value) },
  { label: 'A number', test: (value) => /\d/.test(value) },
]

const buildClubBrand = (sport: ClubSport): SignupBrand => ({
  logo: <SportxWordmark className="text-3xl" />,
  mobileLogo: <SportxWordmark tone="dark" />,
  eyebrow: `${sport.name} clubs & academies`,
  headline: sport.headline,
  description: sport.intro,
  highlights: [
    { icon: CalendarDays, title: 'Classes & schedules', text: 'Batches, sessions and bookings in one calendar.' },
    { icon: UsersRound, title: 'Coaches & players', text: 'Rosters, groups and parents kept connected.' },
    { icon: Trophy, title: 'Matches & events', text: 'Fixtures, tournaments and results in one place.' },
    { icon: CreditCard, title: 'Memberships & fees', text: 'Plans, renewals and payments without spreadsheets.' },
  ],
  trustPoints: [
    { icon: Check, label: 'Set up in minutes' },
    { icon: Check, label: 'No card required' },
    { icon: ShieldCheck, label: 'Role-based access' },
  ],
  gradientClassName: 'from-[#0a1420] via-[#10202c] to-[#0a1420]',
  backdrop: (
    <>
      <img src={sport.image} alt="" aria-hidden className="absolute inset-0 -z-10 h-full w-full object-cover" />
      <div aria-hidden className="absolute inset-0 -z-10 bg-gradient-to-br from-[#0a1420]/95 via-[#0a1420]/85 to-[#0a1420]/60" />
      <p className="absolute right-6 top-6 text-[10px] text-white/45">Photo: {sport.credit}</p>
    </>
  ),
})

interface ParentOrganizationOption {
  value: string
  label: string
  identifier: string
}

interface SignupCompanyPageProps {
  signupPath?: string
  selectedSport?: string
  skipParentOrganizationPrompt?: boolean
}

const toSlug = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

const toParentOrganizationOption = (
  instance: OrgTreeServiceInstance | null | undefined,
): ParentOrganizationOption | null => {
  const value = String(instance?.instance_id ?? instance?.id ?? '').trim()
  if (!instance || !value) {
    return null
  }
  const identifier = String(instance.identifier ?? instance.url_identifier ?? '').trim()
  return {
    value,
    label: String(instance.name || instance.title || identifier || value),
    identifier,
  }
}

export default function SignupCompanyPage({
  signupPath = '/signup/company',
  selectedSport,
  skipParentOrganizationPrompt = false,
}: SignupCompanyPageProps = {}) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const selectedBackendServerUrl = useSelectedBackendServerUrl()
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const envRootOrgReference = ENFORCE_ENV_ROOT_ORG_ID ? SIGNUP_ENV_ROOT_ORG_ID : ''
  const requestedParentOrgReference =
    searchParams.get('org_instance_id') ||
    searchParams.get('parent_org_instance_id') ||
    searchParams.get('org_identifier') ||
    searchParams.get('parent_org_identifier') ||
    searchParams.get('tenant') ||
    ''
  const defaultParentOrgReference = envRootOrgReference || requestedParentOrgReference
  const showParentOrgSelect = !skipParentOrganizationPrompt && !envRootOrgReference

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    watch,
    formState: { errors },
  } = useForm<CompanySignupFormData>({
    resolver: zodResolver(companySignupSchema),
    defaultValues: {
      country_id: '94',
      parent_org_reference: defaultParentOrgReference,
    },
  })

  const companyName = watch('company_name')
  const selectedCountryId = watch('country_id')
  const password = watch('password') ?? ''
  const clubSport = findClubSport(selectedSport)

  const parentOrgQuery = useQuery({
    queryKey: ['company-signup-parent-org', selectedBackendServerUrl, defaultParentOrgReference],
    queryFn: () => orgTreeApi.resolvePublicSignupServiceInstance(defaultParentOrgReference),
    enabled: defaultParentOrgReference.length >= 3,
    staleTime: 60_000,
    retry: false,
    placeholderData: (previousData) => previousData,
  })

  const parentOrganization = parentOrgQuery.data
  const parentOrganizationName = parentOrganization?.name
    || parentOrganization?.title
    || parentOrganization?.identifier
    || parentOrganization?.url_identifier
    || defaultParentOrgReference
    || 'Parent organization'
  const { sidePanelLogos: parentSidePanelLogos } = resolveServiceInstanceAuthLogos(parentOrganization)
  const termsText = parentOrganization?.terms_and_conditions ?? null
  const privacyText = parentOrganization?.privacy_policy ?? null
  const hasParentOrganizationLoadError = Boolean(defaultParentOrgReference)
    && parentOrgQuery.isError
  const hasInvalidParentOrgParam = Boolean(defaultParentOrgReference)
    && !parentOrgQuery.isLoading
    && !parentOrgQuery.isError
    && !parentOrganization

  const parentOptionQueries = useQueries({
    queries: SIGNUP_PARENT_ORGANIZATION_IDENTIFIERS.map((identifier) => ({
      queryKey: ['company-signup-parent-option', selectedBackendServerUrl, identifier],
      queryFn: () => orgTreeApi.getServiceInstanceByIdentifier(identifier),
      enabled: showParentOrgSelect,
      staleTime: 5 * 60_000,
      retry: false,
    })),
  })
  const isLoadingParentOptions = parentOptionQueries.some((query) => query.isLoading)
  const parentOrganizationOptions: ParentOrganizationOption[] = []
  for (const option of [
    ...parentOptionQueries.map((query) => toParentOrganizationOption(query.data)),
    toParentOrganizationOption(parentOrganization),
  ]) {
    if (option && !parentOrganizationOptions.some((existing) => existing.value === option.value)) {
      parentOrganizationOptions.push(option)
    }
  }
  const normalizedParentReference = defaultParentOrgReference.trim().toLowerCase()
  const selectedParentOption = normalizedParentReference
    ? parentOrganizationOptions.find((option) => (
      option.value.toLowerCase() === normalizedParentReference
      || option.identifier.toLowerCase() === normalizedParentReference
    ))
    : undefined

  useRefetchOnReconnect({
    enabled: hasParentOrganizationLoadError,
    refetch: parentOrgQuery.refetch,
  })

  const countriesQuery = useQuery({
    queryKey: ['org-tree-countries', selectedBackendServerUrl],
    queryFn: orgTreeApi.listCountries,
    staleTime: 60_000,
  })

  useEffect(() => {
    if (!companyName) {
      return
    }

    setValue('identifier', toSlug(companyName), { shouldValidate: false, shouldDirty: false })
  }, [companyName, setValue])

  useEffect(() => {
    if (selectedCountryId || !countriesQuery.data?.length) {
      return
    }

    setValue('country_id', String(countriesQuery.data[0].id), { shouldValidate: false, shouldDirty: false })
  }, [countriesQuery.data, selectedCountryId, setValue])

  useEffect(() => {
    if (!defaultParentOrgReference) {
      return
    }

    setValue('parent_org_reference', defaultParentOrgReference, { shouldValidate: false, shouldDirty: false })
  }, [defaultParentOrgReference, setValue])

  const isClubParentSelected = showParentOrgSelect
    && selectedParentOption?.identifier.toLowerCase() === CLUB_PARENT_ORGANIZATION_IDENTIFIER
  useEffect(() => {
    if (isClubParentSelected) {
      navigate('/setup-club-company', { replace: true })
    }
  }, [isClubParentSelected, navigate])

  const selectParentOrganization = (value: string) => {
    setValue('parent_org_reference', value, { shouldValidate: true, shouldDirty: true })
    const nextParams = new URLSearchParams(searchParams)
    for (const key of ['parent_org_instance_id', 'org_identifier', 'parent_org_identifier', 'tenant']) {
      nextParams.delete(key)
    }
    nextParams.set('org_instance_id', value)
    navigate(`${signupPath}?${nextParams.toString()}`, { replace: true })
  }

  const onSubmit = async (data: CompanySignupFormData) => {
    if (showParentOrgSelect && !data.parent_org_reference?.trim()) {
      setError('parent_org_reference', { message: 'Parent organization is required' })
      return
    }
    setIsLoading(true)
    try {
      const signupPayload = {
        company_name: data.company_name,
        identifier: data.identifier,
        owner_name: data.owner_name,
        email: data.email,
        org_instance_id: data.parent_org_reference?.trim() || undefined,
        country_id: Number(data.country_id),
        phone_number: data.phone_number,
        password: data.password,
      }
      const response = selectedSport
        ? await authApi.clubCompanySignup({
            ...signupPayload,
            signup_type: 'club_company',
            branch_name: data.company_name,
            sport: selectedSport,
          })
        : await authApi.signup({
            ...signupPayload,
            signup_type: 'company',
          })
      const loginTenant = response.data.company_identifier || response.data.service_instance_id

      if (isSignupEmailOtpVerificationContext(response.data.context) && data.email.trim()) {
        navigate(buildSignupEmailVerificationPath({
          email: data.email.trim(),
          tenant: loginTenant,
          orgInstanceId: response.data.service_instance_id || loginTenant,
          otpLength: response.data.otp_length,
        }), { replace: true })
        return
      }

      if (loginTenant) {
        try {
          const loginResponse = await authApi.login({
            tenant: loginTenant,
            email: data.email,
            password: data.password,
          })

          if (loginResponse.data?.access_token) {
            toast.success('Account created successfully! Select a post to continue.')
            navigate(buildPostSelectionPath(loginResponse.data.service_instance_id || loginTenant), { replace: true })
            return
          }
        } catch {
          // Signup succeeded; fall through to the sign-in redirect.
        }
      }

      toast.success('Account created successfully! Please sign in.')
      const loginParams = new URLSearchParams()
      if (loginTenant) {
        loginParams.set('tenant', loginTenant)
      }
      navigate(loginParams.toString() ? `/login?${loginParams.toString()}` : '/login')
    } catch (error) {
      toast.error(getErrorMessage(error))
    } finally {
      setIsLoading(false)
    }
  }

  if (clubSport) {
    const selectedCountry = countriesQuery.data?.find((country) => String(country.id) === selectedCountryId)
    return (
      <SignupSplitShell
        brand={buildClubBrand(clubSport)}
        title={`Create your ${clubSport.name.toLowerCase()} club`}
        description="Set up your club workspace and admin account. You'll be signed in straight away."
      >
        <div className="mb-8 flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-2.5 pr-4 shadow-sm">
          <img src={clubSport.image} alt="" className="h-12 w-12 shrink-0 rounded-xl object-cover" />
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Sport</p>
            <p className="truncate text-sm font-bold text-slate-900">{clubSport.name}</p>
          </div>
          <Link to="/setup-club-company" className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline">
            <ArrowLeft className="h-3.5 w-3.5" /> Change
          </Link>
        </div>

        {!ENFORCE_ENV_ROOT_ORG_ID ? <BackendServerSelector disabled={isLoading} className="mb-6" /> : null}
        {hasParentOrganizationLoadError ? (
          <div className="mb-6 rounded-xl border border-amber-300/50 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <p>Unable to verify parent organization details right now. You can still continue with the selected reference.</p>
            <button type="button" onClick={() => { void parentOrgQuery.refetch() }} className="mt-2 font-medium text-primary hover:underline">
              Retry
            </button>
          </div>
        ) : null}
        {hasInvalidParentOrgParam ? (
          <div className="mb-6 rounded-xl border border-amber-300/50 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            Parent organization details could not be verified. Signup will use the entered reference during submission.
          </div>
        ) : null}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8" noValidate>
          <input type="hidden" {...register('parent_org_reference')} />

          <FormSection step={1} title="About your club">
            <div className="space-y-1.5">
              <Label htmlFor="company_name">Club name</Label>
              <IconInput id="company_name" icon={Trophy} placeholder={`City ${clubSport.name} Academy`} disabled={isLoading} {...register('company_name')} aria-invalid={!!errors.company_name} />
              <FieldError message={errors.company_name ? 'Club name is required' : undefined} />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="identifier">Club code</Label>
              <IconInput id="identifier" icon={AtSign} placeholder="city-academy" autoCapitalize="none" spellCheck={false} disabled={isLoading} {...register('identifier')} aria-invalid={!!errors.identifier} />
              {errors.identifier ? (
                <FieldError message="Club code is required" />
              ) : (
                <p className="text-xs text-muted-foreground">Filled in from your club name. Members use it to find your club when they sign in.</p>
              )}
            </div>
          </FormSection>

          <FormSection step={2} title="Your details">
            <div className="space-y-1.5">
              <Label htmlFor="owner_name">Administrator name</Label>
              <IconInput id="owner_name" icon={User} placeholder="Jane Doe" autoComplete="name" disabled={isLoading} {...register('owner_name')} aria-invalid={!!errors.owner_name} />
              <FieldError message={errors.owner_name ? 'Your name is required' : undefined} />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <IconInput id="email" icon={Mail} type="email" placeholder="name@example.com" autoComplete="email" disabled={isLoading} {...register('email')} aria-invalid={!!errors.email} />
              <FieldError message={errors.email?.message} />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="country_id">Country</Label>
                <input type="hidden" {...register('country_id')} />
                <Select value={selectedCountryId || ''} onValueChange={(value) => setValue('country_id', value, { shouldValidate: true, shouldDirty: true })} disabled={isLoading || countriesQuery.isLoading || !countriesQuery.data?.length}>
                  <SelectTrigger id="country_id" aria-invalid={!!errors.country_id} className="!h-11 w-full rounded-xl bg-white">
                    <SelectValue placeholder={countriesQuery.isLoading ? 'Loading countries...' : 'Select a country'} />
                  </SelectTrigger>
                  <SelectContent>
                    {(countriesQuery.data ?? []).map((country) => (
                      <SelectItem key={String(country.id)} value={String(country.id)}>
                        {country.country_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {countriesQuery.isError && !errors.country_id ? <FieldError message="Unable to load countries right now." /> : null}
                <FieldError message={errors.country_id?.message} />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="phone_number">Phone number</Label>
                <PhoneInput id="phone_number" dialCode={selectedCountry?.dial_code} placeholder={PHONE_NUMBER_SAMPLE_PLACEHOLDER} disabled={isLoading} {...register('phone_number')} aria-invalid={!!errors.phone_number} />
                <FieldError message={errors.phone_number?.message} />
              </div>
            </div>
          </FormSection>

          <FormSection step={3} title="Secure your account">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="password">Password</Label>
                <PasswordInput id="password" placeholder="Create a password" disabled={isLoading} {...register('password')} aria-invalid={!!errors.password} />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="password_confirm">Confirm password</Label>
                <PasswordInput id="password_confirm" placeholder="Repeat your password" disabled={isLoading} {...register('password_confirm')} aria-invalid={!!errors.password_confirm} />
                <FieldError message={errors.password_confirm?.message} />
              </div>
            </div>

            <PasswordChecklist rules={PASSWORD_RULES} value={password} showErrors={Boolean(errors.password)} />
          </FormSection>

          <div className="space-y-4">
            <AuthTermsDisclosure termsText={termsText} privacyText={privacyText} />

            <Button type="submit" size="lg" className="h-12 w-full rounded-xl bg-[#0a1420] text-base font-semibold text-white shadow-lg shadow-slate-950/20 hover:bg-[#10202c]" disabled={isLoading}>
              {isLoading ? <Spinner className="mr-2" /> : null}
              {isLoading ? 'Creating your club…' : 'Create club'}
              {isLoading ? null : <ArrowRight className="ml-1 h-4 w-4 text-[#c9fa55]" />}
            </Button>

            <p className="text-center text-sm text-muted-foreground">
              Joining an existing club?{' '}
              <Link to="/signup" className="font-semibold text-primary hover:underline">
                Sign up as a member
              </Link>
            </p>
          </div>
        </form>
      </SignupSplitShell>
    )
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-primary/5 px-4 py-10">
      <div aria-hidden className="pointer-events-none absolute -left-32 top-10 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute -right-28 bottom-8 h-[28rem] w-[28rem] rounded-full bg-primary/10 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute left-1/2 top-1/2 h-[40rem] w-[40rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/10 blur-3xl" />

      <div className="relative mx-auto grid w-full max-w-6xl items-stretch overflow-hidden rounded-[36px] border border-white/70 bg-white/95 shadow-[0_28px_90px_rgba(15,23,42,0.18)] lg:grid-cols-[0.95fr_1.05fr]">
        <section className="relative isolate hidden items-center justify-center overflow-hidden bg-white p-8 lg:flex">
          <div aria-hidden className="absolute -left-24 -top-24 h-72 w-72 rounded-full bg-primary/5 blur-3xl" />
          <div aria-hidden className="absolute -bottom-28 right-8 h-80 w-80 rounded-full bg-primary/5 blur-3xl" />
          <AuthLogoImage
            sources={parentSidePanelLogos}
            alt={`${parentOrganizationName} logo`}
            className="relative z-10 max-h-44 w-auto object-contain drop-shadow-xl sm:max-h-52"
            fallback={(
              <div className="relative z-10 flex h-36 w-36 items-center justify-center rounded-[34px] bg-primary/10 text-primary shadow-sm sm:h-44 sm:w-44">
                <Briefcase className="h-16 w-16" />
              </div>
            )}
          />
        </section>

        <div className="relative isolate flex h-full w-full flex-col overflow-hidden bg-primary p-6 text-primary-foreground sm:p-8">
        <div aria-hidden className="absolute -right-28 -top-24 h-96 w-96 rounded-full bg-white/20" />
        <div aria-hidden className="absolute -bottom-44 right-8 h-[30rem] w-[30rem] rounded-full bg-slate-900/10" />
        <div aria-hidden className="absolute -right-28 top-1/2 h-[34rem] w-[34rem] -translate-y-1/2 rounded-full border border-white/15" />
        <div className="relative z-10 mb-8 flex flex-col items-center text-center">
          <h1 className="text-2xl font-bold text-white">{selectedSport ? `Create your ${selectedSport} club` : 'Create an organization'}</h1>
          <p className="mt-1 text-sm text-white/80">
            {selectedSport ? 'Set up your club company account for the portal.' : 'Register a new organization account for the portal.'}
          </p>
        </div>

        <Card className="relative z-10 rounded-[28px] border-0 bg-white/95 text-foreground shadow-2xl shadow-slate-950/20 backdrop-blur">
          <CardHeader className="space-y-1">
            <CardTitle className="text-xl">
              {selectedSport ? 'Club signup' : 'Organization signup'}
            </CardTitle>
            <CardDescription>
              {selectedSport
                ? 'Set the club name and identifier, then create the initial admin account.'
                : 'Set the organization name and identifier, then create the initial admin account.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!ENFORCE_ENV_ROOT_ORG_ID ? (
              <BackendServerSelector disabled={isLoading} className="mb-4" />
            ) : null}
            {hasParentOrganizationLoadError ? (
              <div className="mb-4 rounded-lg border border-amber-300/50 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                <p>Unable to verify parent organization details right now. You can still continue with the selected reference.</p>
                <button
                  type="button"
                  onClick={() => {
                    void parentOrgQuery.refetch()
                  }}
                  className="mt-2 font-medium text-primary hover:underline"
                >
                  Retry
                </button>
              </div>
            ) : null}
            {hasInvalidParentOrgParam ? (
              <div className="mb-4 rounded-lg border border-amber-300/50 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                Parent organization details could not be verified. Signup will use the entered reference during submission.
              </div>
            ) : null}
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <input type="hidden" {...register('parent_org_reference')} />
              {showParentOrgSelect ? (
                <div className="space-y-2">
                  <Label htmlFor="parent_org_reference">Parent organization</Label>
                  <Select
                    value={selectedParentOption?.value ?? ''}
                    onValueChange={selectParentOrganization}
                    disabled={isLoading || isLoadingParentOptions || !parentOrganizationOptions.length}
                  >
                    <SelectTrigger id="parent_org_reference" aria-invalid={!!errors.parent_org_reference} className="w-full">
                      <SelectValue
                        placeholder={isLoadingParentOptions ? 'Loading organizations...' : 'Select a parent organization'}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {parentOrganizationOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!isLoadingParentOptions && !parentOrganizationOptions.length ? (
                    <p className="text-sm text-destructive">No parent organizations are available on this server.</p>
                  ) : null}
                  {errors.parent_org_reference ? <p className="text-sm text-destructive">{errors.parent_org_reference.message}</p> : null}
                </div>
              ) : defaultParentOrgReference ? (
                <div className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
                  Creating this organization under <span className="font-medium text-foreground">{parentOrganizationName}</span>.
                </div>
              ) : null}

              <div className="space-y-2">
                <Label htmlFor="company_name">Organization name</Label>
                <Input id="company_name" placeholder="Acme Services" disabled={isLoading} {...register('company_name')} aria-invalid={!!errors.company_name} />
                {errors.company_name ? <p className="text-sm text-destructive">{errors.company_name.message}</p> : null}
              </div>

              <div className="space-y-2">
                <Label htmlFor="identifier">Identifier</Label>
                <Input id="identifier" placeholder="acme-services" disabled={isLoading} {...register('identifier')} aria-invalid={!!errors.identifier} />
                {errors.identifier ? <p className="text-sm text-destructive">{errors.identifier.message}</p> : null}
              </div>

              <div className="space-y-2">
                <Label htmlFor="owner_name">Owner name</Label>
                <Input id="owner_name" placeholder="Jane Doe" disabled={isLoading} {...register('owner_name')} aria-invalid={!!errors.owner_name} />
                {errors.owner_name ? <p className="text-sm text-destructive">{errors.owner_name.message}</p> : null}
              </div>

              <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="country_id">Country</Label>
                <input type="hidden" {...register('country_id')} />
                <Select value={selectedCountryId || ''} onValueChange={(value) => setValue('country_id', value, { shouldValidate: true, shouldDirty: true })} disabled={isLoading || countriesQuery.isLoading || !countriesQuery.data?.length}>
                  <SelectTrigger id="country_id" aria-invalid={!!errors.country_id} className="w-full">
                    <SelectValue placeholder={countriesQuery.isLoading ? 'Loading countries...' : 'Select a country'} />
                  </SelectTrigger>
                  <SelectContent>
                    {(countriesQuery.data ?? []).map((country) => (
                      <SelectItem key={String(country.id)} value={String(country.id)}>
                        {[country.country_name, country.dial_code].filter(Boolean).join(' ')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {countriesQuery.isError && !errors.country_id ? <p className="text-sm text-destructive">Unable to load countries right now.</p> : null}
                {errors.country_id ? <p className="text-sm text-destructive">{errors.country_id.message}</p> : null}
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone_number">Phone number</Label>
                <Input id="phone_number" type="tel" placeholder={PHONE_NUMBER_SAMPLE_PLACEHOLDER} autoComplete="tel" disabled={isLoading} {...register('phone_number')} aria-invalid={!!errors.phone_number} />
                {errors.phone_number ? <p className="text-sm text-destructive">{errors.phone_number.message}</p> : null}
              </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" placeholder="name@example.com" autoComplete="email" disabled={isLoading} {...register('email')} aria-invalid={!!errors.email} />
                {errors.email ? <p className="text-sm text-destructive">{errors.email.message}</p> : null}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input id="password" type={showPassword ? 'text' : 'password'} placeholder="Create a password" autoComplete="new-password" disabled={isLoading} {...register('password')} aria-invalid={!!errors.password} />
                  <button type="button" onClick={() => setShowPassword((current) => !current)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" tabIndex={-1}>
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {errors.password ? <p className="text-sm text-destructive">{errors.password.message}</p> : null}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password_confirm">Confirm password</Label>
                <div className="relative">
                  <Input id="password_confirm" type={showConfirmPassword ? 'text' : 'password'} placeholder="Confirm your password" autoComplete="new-password" disabled={isLoading} {...register('password_confirm')} aria-invalid={!!errors.password_confirm} />
                  <button type="button" onClick={() => setShowConfirmPassword((current) => !current)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" tabIndex={-1}>
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {errors.password_confirm ? <p className="text-sm text-destructive">{errors.password_confirm.message}</p> : null}
              </div>

              <AuthTermsDisclosure termsText={termsText} privacyText={privacyText} />

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? <Spinner className="mr-2" /> : null}
                Create organization account
              </Button>
            </form>

            <div className="mt-6 rounded-xl border border-border/70 bg-muted/30 p-4">
              <p className="mb-2 text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground">Other access options</p>
              <div className="space-y-2 text-center text-sm">
              <div>
                <span className="text-muted-foreground">Joining an existing organization? </span>
                <Link to="/signup" className="font-medium text-primary hover:underline">
                  Signup
                </Link>
              </div>
              <div>
                <span className="text-muted-foreground">Already have an account? </span>
                <Link to="/login" className="font-medium text-primary hover:underline">
                  Sign in
                </Link>
              </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      </div>
    </div>
  )
}